import { useEffect, useRef, useState } from 'react';
import styles from './loadingSlips.module.css';
import axios from 'axios';
import Select from "react-select";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUser } from '../../features/userSlice';
import FloatingActions from '../../components/FloatingActions';
import { Truck, Package, RefreshCw, CalendarDays, Building2, Search, ImageIcon } from "lucide-react";
import { usePopup } from '../../utils/PopupContext';


const MyLoadingSlips = () => {
    const user = useSelector(selectUser);
    const token = user?.token;
    const authCfg = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
    const emp_id = user?.emp_id;
    const navigate = useNavigate();
    const topRef = useRef(null);
    const bottomRef = useRef(null);
    const { showPopup } = usePopup();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [loadData, setLoadData] = useState({ loading: false, dataPresent: false });

    const [inDate, setInDate] = useState("");
    const [outDate, setOutDate] = useState("");
    const [loadedTrucksList, setLoadedTrucksList] = useState([]);
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [expandedIndex, setExpandedIndex] = useState(null);

    // Stations mapped to user
    const [loadingStations, setLoadingStations] = useState(false);
    const [stationOptions, setStationOptions] = useState([]);
    const [selectedStation, setSelectedStation] = useState({
        value: "ALL",
        label: "All My Stations",
    });

    const toggleCard = (index) => {
        setExpandedIndex(prev => prev === index ? null : index);
    };

    const formatDateTime = (value) => {
        if (!value) return "-";

        return new Date(value).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true
        });
    };

    const getStatus = (parent) => {
        if (parent.p_outdate || parent.P_SECONDWT) return "Completed";
        // if (parent.p_loading_start) return "Loading";
        return "Pending";
    };

    const formatDates = () => {
        const today = new Date();
        const startDate = today.toISOString().slice(0, 10);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const endDate = tomorrow.toISOString().slice(0, 10);

        setInDate(startDate);
        setOutDate(endDate);
    }

    const buildStationCsv = () => {
        if (!selectedStation || selectedStation.value === "ALL") {
            const ids = stationOptions
                .filter((o) => o.value !== "ALL")
                .map((o) => o.value);

            return ids.join(",");
        }

        return String(selectedStation.value);
    };

    const filterList = (val, list) => {
        const q = (val || "").trim().toLowerCase();

        if (!q) return list;

        return list.filter((s) => {
            const doc = (s.P_DOCNUMBER || "").toLowerCase();
            const party = (s.P_Party_name || "").toLowerCase();

            const adviceMatches = (s.advice || []).some((item) => {
                const vehicle = (item.VEHICLE_NO || "").toLowerCase();
                const itemCode = (item.ITEM_CODE || "").toLowerCase();
                const itemDesc = (item.ITEM_DESC || "").toLowerCase();

                return (
                    vehicle.includes(q) ||
                    itemCode.includes(q) ||
                    itemDesc.includes(q)
                );
            });

            return (
                doc.includes(q) ||
                party.includes(q) ||
                adviceMatches
            );
        });
    };

    const filteredSlips = (val) => {
        setData(filterList(val, loadedTrucksList));
    };

    // ---------- load stations ----------
    const fetchUnloadingPlaces = async () => {
        if (!emp_id) return;

        try {
            setLoadingStations(true);
            const res = await axios.post(
                "/api/wbauto/getUsersUnloadingStations",
                {
                    emp_id,
                    token,
                },
                authCfg
            );

            const data = res.data || [];
            console.log("Stage---0- Stations:", data);

            const formatted = data.map((row) => ({
                value: row.L_PlaceId,
                label: row.L_PlaceName,
                entity_code: row.entity_code,
            }));

            const finalOptions = [
                { value: "ALL", label: "All My Stations" },
                ...formatted,
            ];

            setStationOptions(finalOptions);
            setSelectedStation(finalOptions[0]);
        } catch (err) {
            console.error(
                "Error loading stations:",
                err?.response?.data || err.message
            );
            showPopup({
                variant: "error",
                title: "Something Went Wrong",
                message: "Unable to load assigned stations."
            });
        } finally {
            setLoadingStations(false);
        }
    };

    const fetchData = async (pageNum = 1) => {
        setLoading(true);
        setLoadData((prev) => ({ ...prev, loading: true }));
        setPage(pageNum);

        const payload = {
            inDate,
            outDate,
            station_ids_csv: buildStationCsv(),
            scope: "stations",
            page: pageNum,
            limit: 20
        };

        try {
            const resp = await axios.post('/api/loading/getLoadedTrucks', payload, authCfg);

            if (resp && resp.data && resp.data.success) {
                const newData = resp.data.data || [];

                const arr = pageNum > 1
                    ? [...loadedTrucksList, ...newData]
                    : newData;

                setLoadedTrucksList(arr);
                setData(filterList(searchText, arr));

                if (resp.data.length === 0) {
                    setLoadData((prev) => ({ ...prev, dataPresent: false }));
                } else {
                    setLoadData((prev) => ({ ...prev, dataPresent: true }));
                }
            }
        } catch (error) {
            showPopup({
                variant: "error",
                title: "Something Went Wrong",
                message: error.response?.data?.message || "Unable to load truck information. Please try again."
            });
        } finally {
            setLoading(false);
            setLoadData((prev) => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        formatDates();
    }, []);

    useEffect(() => {
        fetchUnloadingPlaces();
    }, [emp_id]);

    if (!token) {
        return (
            <div className="container py-4 text-center">
                <p>You must be logged in to view this screen.</p>
                <button className="btn btn-secondary" onClick={() => navigate('/')}>Go to Login</button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div ref={topRef} />

            {/* ================= HEADER ================= */}
            <div className={styles.stickyTop}>
                <div className={styles.headerRow}>
                    <div className={styles.titleBlock}>
                        <div className={styles.title}>My Loaded Trucks</div>
                        <div className={styles.subtitle}>All loaded trucks from your assigned stations • Loading • Photos • Advice</div>
                    </div>

                    <div className={styles.summaryPills}>

                        <div className={styles.pill}>
                            <Truck size={16} />
                            <span>
                                Total Cards: <b>{data.length}</b>
                            </span>
                        </div>

                        <div className={`${styles.pill} ${styles.pillMoney}`}>
                            <Package size={16} />
                            <span>
                                Total Advice:{" "}
                                <b>
                                    {data.reduce(
                                        (total, parent) => total + (parent.advice?.length || 0),
                                        0
                                    )}
                                </b>
                            </span>
                        </div>

                        <div className={styles.pill}>
                            <RefreshCw size={16} />
                            <span>
                                Loaded: <b>{data.length}</b>
                            </span>
                        </div>

                    </div>
                </div>

                {/* ================= FILTERS ================= */}
                <div className={styles.filterRow}>

                    <div className={styles.filterItem}>
                        <label>
                            <CalendarDays size={16} />
                            From Date
                        </label>

                        <input
                            type="date"
                            value={inDate}
                            onChange={(e) => {
                                setInDate(e.target.value);
                                fetchData();
                            }}
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.filterItem}>
                        <label>
                            <CalendarDays size={16} />
                            To Date
                        </label>

                        <input
                            type="date"
                            value={outDate}
                            onChange={(e) => setOutDate(e.target.value)}
                            className={styles.input}
                        />
                    </div>

                    <div className={styles.filterItemWide}>
                        <label>
                            <Building2 size={16} />
                            Station
                        </label>
                        <Select
                            isLoading={loadingStations}
                            options={stationOptions}
                            value={selectedStation}
                            onChange={(v) => setSelectedStation(v)}
                            classNamePrefix="rs"
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    minHeight: "38px",
                                    borderRadius: "12px",
                                    borderColor: "#d7dde6",
                                    boxShadow: "none",
                                }),
                            }}
                        />
                    </div>

                    <div className={styles.filterItemWide}>
                        <label style={{ textAlign: "left" }}>
                            <Search size={16} />
                            Search (truck/doc/item/party)
                        </label>

                        <input
                            type="text"
                            placeholder="Type to filter cards..."
                            className={styles.input}
                            value={searchText}
                            onChange={(e) => {
                                const value = e.target.value;
                                setSearchText(value);
                                filteredSlips(value);
                            }}
                        />
                    </div>

                    <div className={styles.btnRow}>
                        <button
                            className={styles.primaryBtn}
                            onClick={() => fetchData(1)}
                            disabled={loading}
                        >
                            {loading ? "Loading..." : "Reload"}
                        </button>

                        <button
                            className={styles.secondaryBtn}
                            onClick={() => setOutDate("")}
                            disabled={loading}
                        >
                            Clear End Date
                        </button>

                        <button
                            className={styles.secondaryBtn}
                            onClick={() => {
                                setSearchText("");
                                setData(loadedTrucksList);
                                formatDates();
                                setSelectedStation({ value: "ALL", label: "All My Stations" });
                            }}
                            disabled={loading}
                        >
                            Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* ================= BODY ================= */}
            <div className={styles.deck}>

                {data.length === 0 && !loading && (
                    <div className={styles.empty}>
                        No loaded trucks found for the selected filters.
                    </div>
                )}

                {data.map((parent, parentIndex) => {

                    const status = getStatus(parent);

                    return (
                        <div
                            key={parent.P_DOCNUMBER || parentIndex}
                            className={styles.card}
                        >

                            {/* ================= CARD TOP ================= */}
                            <div className={styles.cardTop}>

                                <div className={styles.cardLeft}>

                                    <div className={styles.rowLine}>

                                        <Truck size={18} />

                                        <div className={styles.bigText}>
                                            {parent.P_DOCNUMBER || "-"}
                                        </div>

                                        <span className={styles.badge}>
                                            ENTITY: {parent.P_entity_code || "-"}
                                        </span>

                                        <span className={styles.badgeSoft}>
                                            {status}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* ================= PARENT INFO ================= */}
                            <div className={styles.parentAuditRow}>

                                <div>
                                    <span>Loading Start</span>
                                    <strong>
                                        {formatDateTime(parent.p_loading_start)}
                                    </strong>
                                </div>

                                <div>
                                    <span>Loading End</span>
                                    <strong>
                                        {formatDateTime(parent.p_loading_end)}
                                    </strong>
                                </div>

                                <div>
                                    <span>Operator Start</span>
                                    <strong>
                                        {parent.p_load_unload_oper_Start || "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>Operator End</span>
                                    <strong>
                                        {parent.p_load_unload_oper_End || "-"}
                                    </strong>
                                </div>

                                <div>
                                    <span>Out Date</span>
                                    <strong>
                                        {formatDateTime(parent.p_outdate)}
                                    </strong>
                                </div>

                                <div>
                                    <span>Second WT</span>
                                    <strong>
                                        {parent.P_SECONDWT || "-"}
                                    </strong>
                                </div>

                            </div>

                            {/* ================= PHOTOS ================= */}
                            <div className={styles.photoRow}>

                                <div className={styles.photoBlock}>

                                    <div className={styles.photoHeader}>
                                        <ImageIcon size={16} />
                                        <span>Before Photo</span>
                                    </div>

                                    <div
                                        className={`${styles.photoBox} ${parent.p_before_url
                                                ? ""
                                                : styles.photoEmpty
                                            }`}
                                    >
                                        {parent.p_before_url ? (
                                            <img
                                                src={parent.p_before_url}
                                                alt="Before"
                                                className={styles.photoThumb}
                                            />
                                        ) : (
                                            <div className={styles.photoPlaceholder}>
                                                No Photo
                                            </div>
                                        )}
                                    </div>

                                </div>

                                <div className={styles.photoBlock}>

                                    <div className={styles.photoHeader}>
                                        <ImageIcon size={16} />
                                        <span>After Photo</span>
                                    </div>

                                    <div
                                        className={`${styles.photoBox} ${parent.p_after_url
                                                ? ""
                                                : styles.photoEmpty
                                            }`}
                                    >
                                        {parent.p_after_url ? (
                                            <img
                                                src={parent.p_after_url}
                                                alt="After"
                                                className={styles.photoThumb}
                                            />
                                        ) : (
                                            <div className={styles.photoPlaceholder}>
                                                No Photo
                                            </div>
                                        )}
                                    </div>

                                </div>

                            </div>

                            {/* ================= CHILD / ADVICE ================= */}
                            <div className={styles.adviceSection}>

                                <div className={styles.adviceHeader}>
                                    <span className={styles.adviceTitle}>
                                        Advice Details
                                    </span>

                                    <span className={styles.adviceCount}>
                                        {parent.advice?.length || 0}
                                    </span>
                                </div>

                                {parent.advice?.map((child, childIndex) => (
                                    <div
                                        key={`${parent.P_DOCNUMBER}-${childIndex}`}
                                        className={styles.adviceRecord}
                                    >

                                        <div className={styles.adviceExtra}>
                                            <div>
                                                <span>Vehicle</span>
                                                <strong>
                                                    {child.VEHICLE_NO || "-"}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>Loading Point</span>
                                                <strong>
                                                    {child.LOADING_POINT_NAME || "-"}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>Item</span>
                                                <div className='d-flex justify-content-md-center'>
                                                    <strong>
                                                        {child.ITEM_CODE || "-"} | {child.ITEM_DESC || "-"}
                                                    </strong>
                                                </div>
                                            </div>

                                            <div>
                                                <span>Required</span>
                                                <strong>
                                                    {child.QTY_REQUIRED ?? "-"}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>Loaded</span>
                                                <strong>
                                                    {child.QTY_LOADED ?? "-"}
                                                </strong>
                                            </div>
                                        </div>

                                        <div className={styles.adviceExtra}>
                                            <div>
                                                <span>Advice Date</span>
                                                <strong>
                                                    {formatDateTime(child.ADVICE_DATE)}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>Loading Code</span>
                                                <strong>
                                                    {child.LOADING_POINT_CODE || "-"}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>TCODE</span>
                                                <strong>
                                                    {child.P_TCODE || "-"}
                                                </strong>
                                            </div>

                                            <div>
                                                <span>SL No</span>
                                                <strong>
                                                    {child.SLNO ?? childIndex + 1}
                                                </strong>
                                            </div>
                                        </div>

                                    </div>
                                ))}

                                {(!parent.advice ||
                                    parent.advice.length === 0) && (
                                        <div className={styles.noAdvice}>
                                            No advice records available
                                        </div>
                                    )}

                            </div>

                            {/* ================= REMARK ================= */}
                            <div className={styles.remarkSection}>

                                <div className={styles.remarkTitle}>
                                    Loading Remark
                                </div>

                                <div className={styles.remarkText}>
                                    {parent.P_LoadUnloadRemark || "-"}
                                </div>

                            </div>

                        </div>
                    );
                })}

            </div>

            {data.length > 0 && loadData.dataPresent && (
                <button
                    disabled={loadData.loading}
                    onClick={() => fetchData(page + 1)}
                    className={styles.primaryBtn}
                >
                    {loadData.loading ? "Loading..." : "Load More 20"}
                </button>
            )}

            <FloatingActions
                topRef={topRef}
                bottomRef={bottomRef}
            />

            <div ref={bottomRef} />
        </div>
    )
}

export default MyLoadingSlips;